import { Link } from 'react-router-dom';
import { StatusBadge } from '@/components/StatusBadge';
import { formatAgo } from '@/lib/ago';
import { formatDuration } from '@/lib/format';
import type { Deployment } from '@/types/deployment';

export interface RecentDeploymentsPanelProps {
  deployments: Deployment[];
  endpointNames: Record<number, string>;
}

const LIMIT = 8;
const timeOf = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? -Infinity : t;
};

/** Latest deployments, newest first. Wrap in a Panel on the page. */
export function RecentDeploymentsPanel({ deployments, endpointNames }: RecentDeploymentsPanelProps) {
  if (deployments.length === 0) return <p className="text-sm text-muted-foreground">No deployments yet.</p>;

  const rows = [...deployments].sort((a, b) => timeOf(b.created_at) - timeOf(a.created_at)).slice(0, LIMIT);

  return (
    <ul className="divide-y">
      {rows.map((d) => {
        const created = Date.parse(d.created_at);
        const where = `${d.type} · ${endpointNames[d.endpoint_id] ?? '—'}`;
        return (
          <li key={d.id}>
            <Link
              to="/apps?tab=deployments"
              className="flex items-center justify-between gap-3 rounded-sm py-2 outline-none hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex min-w-0 items-center gap-3">
                <StatusBadge status={d.status} className="shrink-0" />
                <div className="min-w-0">
                  <div className="truncate font-mono text-xs">Deployment #{d.id}</div>
                  <div title={where} className="truncate text-xs text-muted-foreground">{where}</div>
                </div>
              </div>
              <div className="shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                <div>{formatAgo(created)}</div>
                {d.completed_at && <div>{formatDuration(Date.parse(d.completed_at) - created)}</div>}
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
